/**
 * Portal Authentication Service (Cognito Cookies Mode B)
 *
 * Handles authentication via the central auth portal (auth.decisionvue.net).
 * The portal manages login against AWS Cognito and sets two cookies:
 *   - cognito_access:  "tenant_id::jwt_token" (HttpOnly, RS256 access token prefixed with tenant ID)
 *   - cognito_refresh: Cognito refresh token (HttpOnly, opaque)
 *
 * This service checks the session by calling the backend /api/session-check,
 * which verifies the cognito_access token via Cognito JWKS.
 *
 * When the access token expires (401), the service calls the auth portal's
 * POST /auth/refresh-session to get a new access token using the refresh cookie.
 * If refresh also fails, the user is redirected to the auth portal.
 */

class PortalAuthService {
  constructor() {
    this.apiUrl = process.env.RISK_API_URL || "http://localhost:2020";
    this.portalUrl =
      process.env.AUTH_PORTAL_URL || "https://staging.vortex.decisionvue.net";
    this._sessionCache = null;
    this._sessionCacheTimestamp = 0;
    this._sessionCacheTTL = 30000; // 30 seconds
    this._refreshing = null; // Deduplication promise for concurrent refreshes
  }

  /**
   * Attempt to refresh the Cognito access token via the auth portal.
   * The portal reads the cognito_refresh cookie, calls Cognito,
   * and sets an updated cognito_access cookie on the response.
   *
   * @returns {Promise<boolean>} true if refresh succeeded
   */
  async refreshToken() {
    // Deduplicate concurrent refresh calls
    if (this._refreshing) {
      return this._refreshing;
    }

    this._refreshing = (async () => {
      try {
        const response = await window.fetch(
          `${this.portalUrl}/api/auth/refresh-session`,
          {
            method: "POST",
            credentials: "include",
          },
        );
        return response.ok;
      } catch (error) {
        console.error("Token refresh failed:", error);
        return false;
      } finally {
        this._refreshing = null;
      }
    })();

    return this._refreshing;
  }

  /**
   * Check if user has a valid session via Cognito cookies.
   * Calls the backend /api/session-check which verifies the cognito_access cookie.
   * If the access token is expired (401), attempts a token refresh before giving up.
   * Results are cached briefly to avoid excessive requests.
   *
   * @returns {Promise<{authenticated: boolean, email?: string, role?: string, groups?: string[], tenant_id?: string, given_name?: string, family_name?: string}>}
   */
  async checkSession() {
    // Return cached result if still fresh

    const now = Date.now();
    if (
      this._sessionCache &&
      now - this._sessionCacheTimestamp < this._sessionCacheTTL
    ) {
      return this._sessionCache;
    }

    try {
      let response = await fetch(`${this.apiUrl}/api/session-check`, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      // If 401, try refreshing the access token
      if (response.status === 401) {
        const refreshed = await this.refreshToken();
        if (refreshed) {
          // Retry session check with the new access token cookie
          response = await fetch(`${this.apiUrl}/api/session-check`, {
            method: "GET",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
          });
        }
      }

      if (response.ok) {
        const data = await response.json();
        this._sessionCache = data;
        this._sessionCacheTimestamp = Date.now();
        return data;
      }

      // Still not authenticated after potential refresh
      this._sessionCache = null;
      this._sessionCacheTimestamp = 0;

      if (response.status === 403) {
        const data = await response.json();
        return { authenticated: false, error: data.error || "Access denied" };
      }

      return { authenticated: false };
    } catch (error) {
      console.error("Session check failed:", error);
      this._sessionCache = null;
      this._sessionCacheTimestamp = 0;
      return { authenticated: false };
    }
  }

  /**
   * Check if the user is authenticated (has valid Cognito cookies).
   * @returns {Promise<boolean>}
   */
  async isAuthenticated() {
    const session = await this.checkSession();
    return session.authenticated === true;
  }

  /**
   * Get the user profile from the session check response.
   * Maps Cognito cookie auth data to the format expected by the Vuex store.
   *
   * @returns {Promise<object|null>} Profile object compatible with store's login action
   */
  async getProfile() {
    const session = await this.checkSession();
    if (!session.authenticated) {
      return null;
    }

    const isWriteCapableRole = ["admin", "clientadmin"].includes(session.role);

    return {
      email: session.email || "",
      username: session.email || "",
      given_name: session.given_name || "",
      family_name: session.family_name || "",
      name: session.given_name && session.family_name
        ? `${session.given_name} ${session.family_name}`
        : session.email || "",
      organization_id: session.organization_id || session.tenant_id || "",
      readOnly: !isWriteCapableRole,
      role: session.role,
      groups: session.groups || [],
    };
  }

  /**
   * Redirect to the auth portal login page.
   * The portal will authenticate the user, set the Cognito cookies,
   * then redirect back to this app.
   *
   * @param {string} [returnUrl] - URL to return to after login. Defaults to current origin.
   */
  redirectToLogin(returnUrl) {
    const redirectTarget = returnUrl || window.location.origin;
    window.location.href = `${
      this.portalUrl
    }/login?appId=dvhe&redirect=${encodeURIComponent(redirectTarget)}`;
  }

  /**
   * Sign out via the auth portal.
   *
   * Redirects to the portal's /auth/logout endpoint. The portal handles
   * everything (Cognito GlobalSignOut, cookie clearing) and drops the user
   * on the login page. After re-login, the portal redirects back to the
   * redirect URL.
   */
  logout() {
    window.localStorage.clear();
    this._sessionCache = null;
    this._sessionCacheTimestamp = 0;

    const redirectTarget = encodeURIComponent(window.location.origin);
    window.location.href = `${this.portalUrl}/api/auth/logout?redirect=${redirectTarget}&appId=dvhe`;
  }

  /**
   * Make an authenticated fetch request.
   * The Cognito cookies are sent automatically via credentials: 'include'.
   * If the request returns 401, attempt a token refresh and retry once.
   * If still 401 after refresh, redirect to the portal login.
   *
   * @param {string} url - The URL to fetch
   * @param {object} [options] - Fetch options
   * @returns {Promise<Response>}
   */
  async fetch(url, options = {}) {
    const fetchOptions = {
      ...options,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(options.headers || {}),
      },
    };

    let response = await window.fetch(url, fetchOptions);

    // If 401, try refreshing the token and retry the request
    if (response.status === 401) {
      const refreshed = await this.refreshToken();
      if (refreshed) {
        response = await window.fetch(url, fetchOptions);
      }
    }

    // If still 401 after refresh attempt, redirect to portal
    if (response.status === 401) {
      console.warn("Session expired, redirecting to auth portal");
      this._sessionCache = null;
      this._sessionCacheTimestamp = 0;
      this.redirectToLogin();
      return new Promise(() => {});
    }

    return response;
  }
}

// Export singleton instance
const portalAuthService = new PortalAuthService();
export default portalAuthService;
